import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronRight, Pin, PinOff } from "lucide-react";
import { getRouteMeta } from "@/lib/routes-meta";
import { useUiStore } from "@/stores/ui-store";
import { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";

export function TopBar() {
  const { t } = useTranslation("admin");
  const location = useLocation();
  const navigate = useNavigate();
  const pinned = useUiStore((s) => s.topBarPinned);
  const togglePin = useUiStore.getState().toggleTopBarPin;
  const [visible, setVisible] = useState(true);
  const lastScrollY = useRef(0);

  // Auto-hide on scroll (only when not pinned)
  useEffect(() => {
    const main = document.querySelector("main");
    if (!main) return;

    const handler = () => {
      const currentPinned = useUiStore.getState().topBarPinned;
      if (currentPinned) {
        setVisible(true);
        return;
      }
      const scrollTop = main.scrollTop;
      setVisible(scrollTop <= lastScrollY.current || scrollTop < 40);
      lastScrollY.current = scrollTop;
    };

    main.addEventListener("scroll", handler, { passive: true });
    return () => main.removeEventListener("scroll", handler);
  }, []);

  // Breadcrumbs from path
  const pathParts = location.pathname.split("/").filter(Boolean);
  const breadcrumbs = pathParts.map((part, idx) => {
    const fullPath = "/" + pathParts.slice(0, idx + 1).join("/");
    const meta = getRouteMeta(fullPath);
    return {
      label: meta ? t(meta.labelKey) : part,
      path: fullPath,
      isLast: idx === pathParts.length - 1,
    };
  });

  return (
    <header
      className={cn(
        "sticky top-0 z-10 flex items-center justify-between px-6 py-2 transition-all duration-200",
        pinned
          ? "bg-background/80 backdrop-blur-sm border-b border-border"
          : "bg-transparent",
        !visible && !pinned && "opacity-0 pointer-events-none",
      )}
    >
      {/* Breadcrumbs */}
      <nav className="flex items-center gap-1 text-sm">
        {breadcrumbs.map((crumb, idx) => (
          <span key={crumb.path} className="flex items-center gap-1">
            {idx > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
            {crumb.isLast ? (
              <span className="font-medium text-foreground">{crumb.label}</span>
            ) : (
              <button
                onClick={() => navigate(crumb.path)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                {crumb.label}
              </button>
            )}
          </span>
        ))}
      </nav>

      {/* Pin button */}
      <button
        onClick={togglePin}
        title={pinned ? "Sblocca barra" : "Blocca barra"}
        className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
      >
        {pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
      </button>
    </header>
  );
}
